// F1 (SD36, brief §1-2): "Connect a model" picker shared by the admin AI & models page (org
// scope, `/orgs/{id}/transcription/providers?slot=llm`) and the account page's personal model
// section (`/me/ai/providers?slot=llm`). The five kinds, their per-kind fields and the
// Test/Activate/Off actions live in exactly one place so the two call sites can't drift.
import { useState } from "react";
import type { LlmProviderConfig, LlmProviderKind, LlmProviderOut, LlmTestOut } from "../api/types";
import { ApiFail } from "../api/client";
import { Badge, Card, Select } from "@grackle/ui";
import { Button, Field, Notice } from "./ui";
import "../admin/admin.css";
import ConfirmDialog from "./ConfirmDialog";

export const LLM_KIND_LABEL: Record<LlmProviderKind, string> = {
  openai_compatible: "OpenAI-compatible",
  anthropic: "Anthropic",
  gemini: "Gemini",
  azure_openai: "Azure OpenAI",
  bedrock: "Amazon Bedrock",
};

export const LLM_KINDS = Object.keys(LLM_KIND_LABEL) as LlmProviderKind[];

export interface KindMeta {
  base: string;
  modelLabel: string;
  modelPlaceholder: string;
  baseLabel: string;
  basePlaceholder: string;
  keyLabel: string;
  keyOptional?: boolean;
}

export const KIND_META: Record<LlmProviderKind, KindMeta> = {
  openai_compatible: {
    base: "https://api.openai.com/v1",
    modelLabel: "Model",
    modelPlaceholder: "gpt-4o-mini",
    baseLabel: "API base URL",
    basePlaceholder: "https://api.openai.com/v1",
    keyLabel: "API key",
    keyOptional: true,
  },
  anthropic: {
    base: "https://api.anthropic.com",
    modelLabel: "Model",
    modelPlaceholder: "claude-… (pick the model you have access to)",
    baseLabel: "API base URL",
    basePlaceholder: "https://api.anthropic.com",
    keyLabel: "API key",
  },
  gemini: {
    base: "https://generativelanguage.googleapis.com",
    modelLabel: "Model",
    modelPlaceholder: "gemini-…",
    baseLabel: "API base URL",
    basePlaceholder: "https://generativelanguage.googleapis.com",
    keyLabel: "API key",
  },
  azure_openai: {
    base: "",
    modelLabel: "Deployment name",
    modelPlaceholder: "my-gpt4o-deployment",
    baseLabel: "Resource endpoint",
    basePlaceholder: "https://my-resource.openai.azure.com",
    keyLabel: "API key",
  },
  bedrock: {
    base: "https://bedrock-runtime.us-east-1.amazonaws.com",
    modelLabel: "Model id",
    modelPlaceholder: "anthropic.claude-3-5-sonnet-20241022-v2:0",
    baseLabel: "API base URL",
    basePlaceholder: "https://bedrock-runtime.us-east-1.amazonaws.com",
    keyLabel: "Secret access key",
  },
};

export interface LlmConnectBody {
  kind: LlmProviderKind;
  label: string;
  api_base_url?: string;
  model_id?: string;
  api_key?: string;
  config?: Record<string, unknown>;
}

export interface LlmUpdateBody {
  label?: string;
  api_base_url?: string;
  model_id?: string;
  api_key?: string;
  config?: Record<string, unknown>;
}

function kindLabel(kind: string): string {
  return LLM_KIND_LABEL[kind as LlmProviderKind] ?? kind;
}

/** Per-kind extra fields beyond base/model/key, as a plain object ready for `config`. */
function KindExtraFields({
  kind,
  extra,
  setExtra,
}: {
  kind: LlmProviderKind;
  extra: Record<string, string>;
  setExtra: (next: Record<string, string>) => void;
}) {
  function set(key: string, value: string) {
    setExtra({ ...extra, [key]: value });
  }

  if (kind === "azure_openai") {
    return (
      <Field
        label="API version"
        placeholder="2024-02-01"
        value={extra.api_version ?? ""}
        onChange={(e) => set("api_version", e.target.value)}
        help="Defaults to 2024-02-01 when left blank."
      />
    );
  }
  if (kind === "bedrock") {
    return (
      <>
        <div className="admin-two-col">
          <Field
            label="AWS region"
            placeholder="us-east-1"
            value={extra.region ?? ""}
            onChange={(e) => set("region", e.target.value)}
          />
          <Field
            label="Access key id"
            placeholder="AKIA…"
            value={extra.access_key_id ?? ""}
            onChange={(e) => set("access_key_id", e.target.value)}
          />
        </div>
        <Field
          label="Session token (optional)"
          type="password"
          autoComplete="off"
          placeholder="Only needed for temporary STS credentials"
          value={extra.session_token ?? ""}
          onChange={(e) => set("session_token", e.target.value)}
          help="Write-only, like the secret access key above."
        />
      </>
    );
  }
  if (kind === "openai_compatible") {
    return (
      <div className="admin-two-col">
        <Field
          label="Extra header name (optional)"
          placeholder="HTTP-Referer"
          value={extra.header_name ?? ""}
          onChange={(e) => set("header_name", e.target.value)}
        />
        <Field
          label="Extra header value"
          placeholder="https://your-app.example"
          value={extra.header_value ?? ""}
          onChange={(e) => set("header_value", e.target.value)}
        />
      </div>
    );
  }
  return null;
}

export function buildConfig(
  kind: LlmProviderKind,
  extra: Record<string, string>,
  contextTokens: string,
  requestTimeout = "",
): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  if (kind === "azure_openai" && extra.api_version?.trim()) {
    config.api_version = extra.api_version.trim();
  }
  if (kind === "bedrock") {
    if (extra.region?.trim()) config.region = extra.region.trim();
    if (extra.access_key_id?.trim()) config.access_key_id = extra.access_key_id.trim();
    if (extra.session_token?.trim()) config.session_token = extra.session_token.trim();
  }
  if (kind === "openai_compatible" && extra.header_name?.trim() && extra.header_value?.trim()) {
    config.extra_headers = { [extra.header_name.trim()]: extra.header_value.trim() };
  }
  const n = Number(contextTokens.trim());
  if (contextTokens.trim() && Number.isFinite(n) && n > 0) {
    config.context_tokens = Math.round(n);
  }
  const secs = parseTimeout(requestTimeout);
  if (secs !== undefined) config.request_timeout_secs = secs;
  return config;
}

/** A request-timeout field's value as whole seconds, or undefined when blank or not a number
 * (the server then uses its default; it also enforces the 10–1800 range). */
export function parseTimeout(raw: string): number | undefined {
  const n = Number(raw.trim());
  return raw.trim() && Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

const TIMEOUT_HELP = "Seconds, 10 to 1800. Leave blank for 5 minutes. Raise it for a slow or self-hosted model.";

export default function LlmProviderPicker({
  providers,
  onConnect,
  onUpdate,
  onDelete,
  onTest,
  onActivate,
  copyLine,
  connectCollapsed = false,
}: {
  providers: LlmProviderOut[];
  onConnect: (body: LlmConnectBody) => Promise<void>;
  onUpdate: (id: string, body: LlmUpdateBody) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onTest: (id: string) => Promise<LlmTestOut>;
  onActivate: (id: string) => Promise<void>;
  /** Admin page's "Grackle has no built-in language model…" copy line, shown above the list. */
  copyLine?: React.ReactNode;
  /** Hide the connect form behind a "Connect a model" button, with Cancel to close it again (admin
   * AI and models). The account page leaves it open. */
  connectCollapsed?: boolean;
}) {
  const [kind, setKind] = useState<LlmProviderKind>("openai_compatible");
  const [label, setLabel] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState(KIND_META.openai_compatible.base);
  const [modelId, setModelId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [contextTokens, setContextTokens] = useState("");
  const [requestTimeout, setRequestTimeout] = useState("");
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [disconnectId, setDisconnectId] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState(!connectCollapsed);

  function resetConnectForm() {
    onKindChange("openai_compatible");
    setLabel("");
    setApiKey("");
    setContextTokens("");
    setRequestTimeout("");
    setConnectError(null);
  }

  function onCancelConnect() {
    resetConnectForm();
    setConnectOpen(false);
  }

  function onKindChange(next: LlmProviderKind) {
    setKind(next);
    setApiBaseUrl(KIND_META[next].base);
    setModelId("");
    setExtra({});
  }

  async function onConnectClick() {
    setConnecting(true);
    setConnectError(null);
    try {
      await onConnect({
        kind,
        label: label.trim(),
        api_base_url: apiBaseUrl.trim() || undefined,
        model_id: modelId.trim() || undefined,
        api_key: apiKey.trim(),
        config: buildConfig(kind, extra, contextTokens, requestTimeout),
      });
      setApiKey("");
      setLabel("");
      setModelId("");
      setExtra({});
      setContextTokens("");
      if (connectCollapsed) setConnectOpen(false);
    } catch (err) {
      setConnectError(err instanceof ApiFail ? err.message : "Couldn't connect that model.");
    } finally {
      setConnecting(false);
    }
  }

  async function onDisconnectConfirm() {
    if (!disconnectId) return;
    const id = disconnectId;
    setDisconnectId(null);
    try {
      await onDelete(id);
    } catch (err) {
      setConnectError(err instanceof ApiFail ? err.message : "Couldn't turn that model off.");
    }
  }

  const meta = KIND_META[kind];
  const keyMissing = !meta.keyOptional && !apiKey.trim();

  return (
    <div>
      {copyLine ? (
        <div className="admin-llm-copyline">
          <Notice>{copyLine}</Notice>
        </div>
      ) : null}
      <div className="admin-row-sub" style={{ marginBottom: 12 }}>
        API keys are write-only. Grackle stores them encrypted and never shows them again — only
        the last four characters.
      </div>

      {providers.length === 0 ? (
        <div className="admin-row-sub" style={{ marginBottom: 14 }}>
          No model connected.
        </div>
      ) : (
        <div className="admin-provider-list">
          {providers.map((p) => (
            <LlmProviderCard
              key={p.id}
              provider={p}
              onUpdate={(body) => onUpdate(p.id, body)}
              onTest={() => onTest(p.id)}
              onActivate={() => onActivate(p.id)}
              onDisconnect={() => setDisconnectId(p.id)}
            />
          ))}
        </div>
      )}

      {connectOpen ? (
        <div className="admin-provider-connect">
          <h4 className="admin-subhead">Connect a model</h4>
          <Select
            id="llm-kind"
            className="admin-field--narrow"
            label="Kind"
            value={kind}
            onChange={(v) => onKindChange(v as LlmProviderKind)}
            options={LLM_KINDS.map((k) => ({ value: k, label: LLM_KIND_LABEL[k] }))}
          />
          <div className="admin-two-col">
            <Field
              label="Label (optional)"
              placeholder="Production model"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Field
              label={meta.modelLabel}
              placeholder={meta.modelPlaceholder}
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
            />
          </div>
          <Field
            label={meta.baseLabel}
            placeholder={meta.basePlaceholder}
            value={apiBaseUrl}
            onChange={(e) => setApiBaseUrl(e.target.value)}
          />
          <Field
            label={meta.keyLabel}
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            help={meta.keyOptional ? "Optional for a local, unauthenticated host." : "Stored encrypted. Never returned after save."}
          />
          <KindExtraFields kind={kind} extra={extra} setExtra={setExtra} />
          <Field
            label="Context window override (optional)"
            placeholder="e.g. 128000"
            inputMode="numeric"
            value={contextTokens}
            onChange={(e) => setContextTokens(e.target.value)}
            help="Tokens. Leave blank to use this kind's default."
          />
          <Field
            label="Request timeout (optional)"
            placeholder="e.g. 600"
            inputMode="numeric"
            value={requestTimeout}
            onChange={(e) => setRequestTimeout(e.target.value)}
            help={TIMEOUT_HELP}
          />
          {connectError ? <Notice tone="error">{connectError}</Notice> : null}
          <div className="admin-provider-actions">
            <Button onClick={() => void onConnectClick()} loading={connecting} disabled={!modelId.trim() || keyMissing}>
              Connect
            </Button>
            {connectCollapsed ? (
              <Button variant="ghost" onClick={onCancelConnect} disabled={connecting}>
                Cancel
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="admin-stack">
          {/* A failed turn-off reports here while the connect form is closed. */}
          {connectError ? <Notice tone="error">{connectError}</Notice> : null}
          <div>
            <Button variant="secondary" icon="plus" onClick={() => setConnectOpen(true)}>
              Connect a model
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={disconnectId !== null}
        title="Turn this model off"
        message="Turn this model off? Meeting notes stop generating until another model is connected and active. Nothing is deleted from where it came from."
        confirmLabel="Turn off"
        danger
        onClose={() => setDisconnectId(null)}
        onConfirm={() => void onDisconnectConfirm()}
      />
    </div>
  );
}

function LlmProviderCard({
  provider,
  onUpdate,
  onTest,
  onActivate,
  onDisconnect,
}: {
  provider: LlmProviderOut;
  onUpdate: (body: LlmUpdateBody) => Promise<void>;
  onTest: () => Promise<LlmTestOut>;
  onActivate: () => Promise<void>;
  onDisconnect: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(provider.label);
  const [apiBaseUrl, setApiBaseUrl] = useState(provider.api_base_url);
  const [modelId, setModelId] = useState(provider.model_id);
  const [requestTimeout, setRequestTimeout] = useState(String(provider.config.request_timeout_secs ?? ""));
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<LlmTestOut | null>(null);

  const timeoutChanged = parseTimeout(requestTimeout) !== provider.config.request_timeout_secs;

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      await onUpdate({
        label: label.trim(),
        api_base_url: apiBaseUrl.trim(),
        model_id: modelId.trim(),
        api_key: apiKey.trim() ? apiKey.trim() : undefined,
        config: timeoutChanged ? withTimeout(provider.config, requestTimeout) : undefined,
      });
      setApiKey("");
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save that model.");
    } finally {
      setSaving(false);
    }
  }

  async function onTestClick() {
    setTesting(true);
    setError(null);
    setTest(null);
    try {
      setTest(await onTest());
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't reach that model.");
    } finally {
      setTesting(false);
    }
  }

  async function onActivateClick() {
    setActivating(true);
    setError(null);
    try {
      await onActivate();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't activate that model.");
    } finally {
      setActivating(false);
    }
  }

  return (
    <Card size="sm" className="admin-provider">
      <div className="admin-provider-card-head">
        <div>
          <div className="admin-row-name">{provider.label || kindLabel(provider.kind)}</div>
          <div className="admin-row-sub">
            {kindLabel(provider.kind)} · <span className="mono">{provider.model_id}</span>
          </div>
        </div>
        <div className="admin-provider-card-meta">
          {provider.api_key_last4 ? (
            <span className="mono admin-provider-last4" title="Last four of the stored key">
              •••• {provider.api_key_last4}
            </span>
          ) : null}
          {provider.is_active ? <Badge size="sm" tone="success">Active</Badge> : null}
        </div>
      </div>

      {editing ? (
        <div style={{ marginTop: 12 }}>
          <div className="admin-two-col">
            <Field label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
            <Field label="Model id" value={modelId} onChange={(e) => setModelId(e.target.value)} />
          </div>
          <Field label="API base URL" value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} />
          <Field
            label="API key"
            type="password"
            autoComplete="off"
            placeholder="Unchanged — leave blank to keep it"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <Field
            label="Request timeout (optional)"
            placeholder="e.g. 600"
            inputMode="numeric"
            value={requestTimeout}
            onChange={(e) => setRequestTimeout(e.target.value)}
            help={TIMEOUT_HELP}
          />
        </div>
      ) : (
        <div className="admin-row-sub" style={{ marginTop: 8 }}>
          <span className="mono">{provider.api_base_url}</span>
          {providerConfigSummary(provider.config)}
        </div>
      )}

      {test ? (
        <Notice tone={test.ok ? "default" : "error"}>
          {test.ok
            ? `Reached ${test.endpoint}. Model ${test.configured_model} answered in ${test.latency_ms ?? "?"}ms${
                test.json_mode ? ", JSON mode supported" : ""
              }.`
            : test.error ?? "Test failed."}
        </Notice>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="admin-provider-actions">
        {editing ? (
          <>
            <Button onClick={() => void onSave()} loading={saving}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        <Button variant="secondary" onClick={() => void onTestClick()} loading={testing}>
          Test
        </Button>
        {!provider.is_active ? (
          <Button variant="secondary" onClick={() => void onActivateClick()} loading={activating}>
            Activate
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onDisconnect}>
          Off
        </Button>
      </div>
    </Card>
  );
}

/** The stored config with the timeout replaced. PATCH replaces `config` whole, so the rest is
 * sent back as stored; a sealed session token is not in it and the server keeps it. */
export function withTimeout(config: LlmProviderConfig, raw: string): Record<string, unknown> {
  const { session_token_set: _sealed, request_timeout_secs: _old, ...rest } = config;
  const secs = parseTimeout(raw);
  return secs === undefined ? rest : { ...rest, request_timeout_secs: secs };
}

function providerConfigSummary(config: LlmProviderConfig): React.ReactNode {
  const bits: string[] = [];
  if (config.region) bits.push(config.region);
  if (config.api_version) bits.push(`api ${config.api_version}`);
  if (config.context_tokens) bits.push(`${config.context_tokens.toLocaleString()} ctx`);
  if (config.request_timeout_secs) bits.push(`${config.request_timeout_secs}s timeout`);
  if (!bits.length) return null;
  return <> · {bits.join(" · ")}</>;
}
